import { query } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Message, MessageService, TreeNode } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { combineLatest } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { UnsubscribeComponent } from '../../generals/Components';
import { convertToTreeNodes, formatDate, formatDateS, getSelectedTreeNodes, treePartialNodes, treeSelectOnlyLeafs } from '../../helpers/helper';
import { Category, LabelValueNumber, ProductAttribute, ProductAttributeFormat, ProductAttributeValue, ProductEditor, ProductEditorPrice, ProductEditorPriceExtend, ProductPriceType, ProductStatus } from '../../interfaces/interfaces';
import { ConfirmComponent } from '../confirm/confirm.component';

@Component({
  selector: 'app-producteditor',
  templateUrl: './producteditor.component.html',
  styleUrls: ['./producteditor.component.scss']
})
export class ProducteditorComponent extends UnsubscribeComponent implements OnInit, AfterViewInit {

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  productAttributeFormat = ProductAttributeFormat;
  productStatus = ProductStatus;
  tagsSuggestions: string[] = [];

  productStatuses: {label: string, value: string}[] = [
    {label: 'Még nem árult', value: 'NOT_FOR_SALE_YET'},
    {label: 'Előnézetes', value: 'PREVIEW'},
    {label: 'Árult', value: 'FOR_SALE'},
    {label: 'Kifutott', value: 'DISCONTINUED'},
    {label: 'Törölt', value: 'DELETED'}
  ];

  info: Message[] = [{
    severity: 'info',
    detail: 'Termékcímkék hozzáadásához írd be a megfelelő címkét a beviteli mezőbe, majd az ENTER vagy vessző gombok valamelyikével add hozzá a címke listához. Egy címke tartalmazhat szóközt, de vesszőt nem.'
  }];

  productId: number = null;
  dataLoading = false;
  productEditor: ProductEditor = null;
  productEditorForm: FormGroup = new FormGroup({
    productLangData: new FormGroup({
      languageId: new FormControl('hu'),
      name: new FormControl('', [Validators.required]),
      shortDescription: new FormControl(''),
      description: new FormControl(''),
      tags: new FormControl([])
    }),
    status: new FormControl('NOT_FOR_SALE_YET'),
    pcode: new FormControl('', [Validators.required]),
    sellingUnit: new FormControl(1, [Validators.required]),
    unit: new FormControl('', [Validators.required]),
    minimumBuyable: new FormControl(1),
    maximumBuyable: new FormControl(null),               
    newProduct: new FormControl(false),
    newFromDate: new FormControl(null),
    newToDate: new FormControl(null),
    brand: new FormControl(''),
    barcode: new FormControl(null),
    width: new FormControl(0),
    length: new FormControl(0),
    height: new FormControl(0),
    weightNet: new FormControl(0),
    weightGross: new FormControl(0),
    vatRate: new FormControl(27),
    rating: new FormControl(1),
    externalVideoLink: new FormControl(''),
    variationGroupId: new FormControl(null),
    categories: new FormControl([]),
    categoriesTouched: new FormControl(false),
    languagesTouched: new FormControl(false),
    quantitativeDiscounts: new FormControl([]),
    quantitativeDiscountsTouched: new FormControl(false),
    attributes: new FormControl([]),
    attributesTouched: new FormControl(false),
    source: new FormControl(''),
    mainCategoryId: new FormControl(null),
    displayInFeed: new FormControl(false)
  });
  loading = true;

  categories: Category[] = [];
  categoriesTreeNodes: TreeNode<Category>[] = [];
  selectedCategories: TreeNode<Category>[] = [];

  units: string[] = [];
  filteredUnits: string[] = [];
  brands: string[] = [];
  filteredBrands: string[] = [];
  attributes: ProductAttribute[] = [];
  attributesValues: ProductAttributeValue[] = [];
  tags: string[] = [];
  selectedCategoriesDropdownItems: LabelValueNumber[] = [];

  prices: ProductEditorPriceExtend[] = [];

  ngOnInit(): void {
    const urlId: string = this.activatedRoute.snapshot.paramMap.get('id');
    if (urlId !== null) {
      this.productId = parseInt(urlId, 10);
    }
    // TODO: resolvers
    if (this.productId === null) {
      combineLatest([
        this.apiService.getCategories(),
        this.apiService.initProductsEditor(),
        this.apiService.getSimpleTable<ProductPriceType>('productpricetypes')
      ]).subscribe(([{categories}, {brands, units}, {data}]) => {
        this.categories = categories;
        this.categoriesTreeNodes = convertToTreeNodes<Category>(this.categories);
        this.brands = brands;
        this.units = units;
        this.createPrices(data);
      }).add(() => {
        this.loading = false;
      });
    } else {
      combineLatest([
        this.apiService.getCategories(),
        this.apiService.initProductsEditor(),
        this.apiService.getSimpleTable<ProductPriceType>('productpricetypes'),
        this.apiService.getProduct(this.productId)
      ]).subscribe(([{categories}, {brands, units}, {data}, {productEditor}]) => {
        this.categories = categories;
        this.categoriesTreeNodes = convertToTreeNodes<Category>(this.categories);
        treeSelectOnlyLeafs(this.categoriesTreeNodes);
        this.brands = brands;
        this.units = units;
        this.createPrices(data, productEditor);
        this.productEditor = productEditor;
        this.attributesValues = this.productEditor.attributes;
        this.productEditor.newFromDate = formatDateS(this.productEditor.newFromDate);
        this.productEditor.newToDate = formatDateS(this.productEditor.newToDate);
        this.productEditorForm.patchValue(this.productEditor);
        this.productEditorForm.get('productLangData').patchValue(this.productEditor.languages[0]);
        this.selectedCategories = getSelectedTreeNodes(this.categoriesTreeNodes, this.productEditor.categories);
        this.tags = this.productEditor.languages[0].tags;
        treePartialNodes(this.categoriesTreeNodes, this.selectedCategories, false);
        this.getAttributesByCategories(this.productEditor.categories);
        this.createSelectedCategoriesDropdownItems();
      }).add(() => {
        this.loading = false;
      });
    }
  }

  createPrices(productPriceTypes: ProductPriceType[], productEditor: ProductEditor | null = null): void {
    if (productEditor !== null) {
      productEditor.prices.forEach((productEditorPrice: ProductEditorPrice) => {
        if (productEditorPrice.fixedUntil !== null) {
          productEditorPrice.fixedUntil = formatDateS(productEditorPrice.fixedUntil);
        }
      });
    }
    this.prices = productPriceTypes.map((productPriceType: ProductPriceType) => ({
      productPriceType,
      productEditorPrice: productEditor === null ?
        {listPrice: null, priceId: productPriceType.id, salePrice: null, fixedUntil: null} :
        productEditor.prices.find((productEditorPrice: ProductEditorPrice) => productEditorPrice.priceId === productPriceType.id) || {listPrice: null, priceId: productPriceType.id, salePrice: null, fixedUntil: null}
    }));
  }

  save(close: boolean = false): void {
    if (this.productEditorForm.invalid) {
      return;
    }
    const productEditor: any = this.productEditorForm.getRawValue();
    productEditor.attributes = this.attributesValues;
    productEditor.categories = this.getSelectedCategoryIds();
    productEditor.newFromDate = formatDate(this.productEditorForm.get('newFromDate').value);
    productEditor.newToDate = formatDate(this.productEditorForm.get('newToDate').value);
    productEditor.languages = [{...this.productEditorForm.get('productLangData').value as any}];
    productEditor.languages[0].tags = this.tags;
    productEditor.languagesTouched = this.productEditorForm.get('productLangData').touched;
    productEditor.prices = this.prices.map((p) => ({
      ...p.productEditorPrice,
      fixedUntil: p.productEditorPrice.fixedUntil instanceof Date ? formatDate(p.productEditorPrice.fixedUntil) : null
    }));
    if (productEditor.barcode !== null) {
      productEditor.barcode = productEditor.barcode.toString();
    } else {
      productEditor.barcode = '';
    }
    delete productEditor.productLangData;
    if (this.productId === null) {
      this.apiService.createProduct(productEditor).subscribe(({id}) => {
        this.productId = id;
        this.messageService.add({
          severity: 'success',
          summary: 'Termék létrehozás',
          detail: 'Sikeres létrehozás!'
        });
        if (close) {
          this.back();
        }
      })
    } else {
      this.apiService.updateProduct(this.productId, productEditor).subscribe(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Termék szerkeszétes',
          detail: 'Sikeres mentés!'
        });
        if (close) {
          this.back();
        }
      });
    }
  }

  back(): void {
    this.router.navigateByUrl('/products');
  }

  getAttributeValue(attributeId: number): string {
    const attributeValues: string[] = this.attributesValues.filter(av => av.attributeId === attributeId).map(av => av.value);
    if (attributeValues.length > 0) {
      return attributeValues[0];
    }
    return '';
  }

  getAttributeValueNumber(attributeId: number): number {
    const attributeValues: string[] = this.attributesValues.filter(av => av.attributeId === attributeId).map(av => av.value);
    if (attributeValues.length > 0) {
      return parseFloat(attributeValues[0]);
    }
    return null;
  }

  getAttributeValueBoolean(attributeId: number): boolean {
    const attributeValues: string[] = this.attributesValues.filter(av => av.attributeId === attributeId).map(av => av.value);
    if (attributeValues.length > 0) {
      return attributeValues[0] === 'TRUE';
    }
    return false;
  }

  getSelectedCategoryIds(): number[] {
    return this.selectedCategories.filter(node => node.children.length === 0).map(treeNode => treeNode.data.id);
  }

  categoriesSelectionChange(): void {

    // MAIN CATEGORY
    const actualMainCategoryId: number = this.productEditorForm.get('mainCategoryId').value;
    this.createSelectedCategoriesDropdownItems();
    if (this.selectedCategoriesDropdownItems.findIndex((selectedCategoriesDropdownItem: LabelValueNumber) => selectedCategoriesDropdownItem.value === actualMainCategoryId) === -1) {
      this.productEditorForm.get('mainCategoryId').setValue(null);
    }

    this.productEditorForm.get('categoriesTouched').setValue(true);
    this.productEditorForm.get('attributesTouched').setValue(true);
    const categoryIds: number[] = this.getSelectedCategoryIds();
    if (categoryIds.length === 0) {
      this.attributes = [];
    } else {
      this.getAttributesByCategories(categoryIds);
    }
  }

  getAttributesByCategories(categoryIds: number[]): void {
    this.apiService.getAttributesByCategories(categoryIds).subscribe(({attributes}) => {
      this.attributes = attributes;
    });
  }

  filterUnits(event: {originalEvent: InputEvent, query: string}): void {
    this.filteredUnits = this.units.filter(unit => unit.toLowerCase().startsWith(event.query.toLowerCase()));
  }

  filterBrands(event: {originalEvent: InputEvent, query: string}): void {
    this.filteredBrands = this.brands.filter(brand => brand.toLowerCase().startsWith(event.query.toLowerCase()));
  }

  attributesValueChanged(attributeId, value: any): void {
    value = value.toString();
    this.productEditorForm.get('attributesTouched').setValue(true);
    const attributesEditorIndex: number = this.attributesValues.findIndex(attributesValue => attributesValue.attributeId === attributeId);
    if (attributesEditorIndex !== -1) {
      this.attributesValues[attributesEditorIndex].value = value;
    } else {
      this.attributesValues.push({
        attributeId,
        value
      });
    }
  }

  searchTags(query: string): void {
    this.apiService.tags(query).pipe(takeUntil(this.unsubscribe)).subscribe((rsp) => {
      this.tagsSuggestions = rsp.tags;
    });
  }

  selectTag(tag: string): void {
    this.addTag(tag);
    this.productEditorForm.get('productLangData').get('tags').setValue([]);
  }

  selectTagEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      let tag: string = (event.target as HTMLInputElement).value;
      if (tag[tag.length - 1] === ',') {
        tag = tag.substr(0, tag.length - 1);
      }
      this.addTag(tag);
      (event.target as HTMLInputElement).value = '';
    }
  }

  addTag(tag: string): void {
    if (this.tags.indexOf(tag) === -1 && tag !== '') {
      this.tags.push(tag);
      this.productEditorForm.markAsDirty();
    }
  }

  removeTag(index: number): void {
    this.dialogService.open(ConfirmComponent, {
      data: {
        title: 'Címke törlése',
        text: 'Biztosan törlöd ezt a címkét?',
        acceptText: 'Törlés',
        declineText: 'Mégsem'
      }
    }).onClose.pipe(filter(v => v === true)).subscribe(() => {
      this.tags.splice(index, 1);
      this.productEditorForm.markAsDirty();
    });
  }

  createSelectedCategoriesDropdownItems(): void {
    this.selectedCategoriesDropdownItems = [
      { label: 'Nincs kiválasztva', value: null },
      ...this.selectedCategories.map((treeNode: TreeNode<Category>) => ({
        label: treeNode.data.name,
        value: treeNode.data.id
      }))
    ];
  }

  ngAfterViewInit(): void {
    this.input.nativeElement.focus();
  }

  constructor(
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private dialogService: DialogService
  ) {
    super();
  }
}
